import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DocumentDto, PhaseId, ScanStatus } from '@/api/generated';
import { useDocuments, useDownloadDocumentsZip, useUploadDocument } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import {
  SideNav,
  SideNavChip,
  SideNavChips,
  SideNavItem,
  SideNavSection,
} from '@/design-system/chrome/SideNav';
import { Icon, type IconName } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { toast } from '@/design-system/primitives/Toast/Toast';
import { formatDayMonth, toNumber } from '@/lib/format';
import { DocumentUploadDialog } from './DocumentDialogs';
import styles from './Documents.module.css';

const PHASE_FILTERS: Array<{ label: string; id?: PhaseId; icon: IconName }> = [
  { label: 'Alle fases', icon: 'folder' },
  { label: 'Voorbereiding', id: 'Voor', icon: 'flag' },
  { label: 'Vergunningen', id: 'Verg', icon: 'shield' },
  { label: 'Ruwbouw', id: 'Ruw', icon: 'arch' },
  { label: 'Wind-/waterdicht', id: 'Wwd', icon: 'archive' },
  { label: 'Technieken', id: 'Tech', icon: 'tool' },
];

const PHASE_LABEL: Record<string, string> = {
  Voor: 'Voorber.',
  Ontw: 'Ontwerp',
  Verg: 'Vergunningen',
  Aanb: 'Aanbest.',
  Ruw: 'Ruwbouw',
  Wwd: 'W&W-d.',
  Tech: 'Technieken',
  Afw: 'Afwerking',
  Opl: 'Oplevering',
  Naz: 'Nazorg',
};

export function Documents() {
  const [phaseFilter, setPhaseFilter] = useState<PhaseId | undefined>(undefined);
  const [tagFilter, setTagFilter] = useState<string | undefined>(undefined);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const dragDepth = useRef(0);
  const upload = useUploadDocument();
  const downloadZip = useDownloadDocumentsZip();
  const toggleSelected = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);
  const docs = useDocuments({
    phaseId: phaseFilter,
    ...(tagFilter ? { tag: tagFilter } : {}),
    pageSize: 50,
  });
  // Unfiltered fetch backing the sidebar "Tags" facet so the chip list stays
  // stable when the user clicks a phase or another tag. Capped at 500 (matches
  // the API's MaxPageSize) — projects much larger than that should swap this
  // for a dedicated /api/documents/tags endpoint.
  const allDocs = useDocuments({ pageSize: 500 });
  const availableTags = useMemo(() => {
    const seen = new Set<string>();
    for (const d of allDocs.data?.items ?? []) {
      for (const t of d.tags ?? []) seen.add(t);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b, 'nl-BE'));
  }, [allDocs.data]);

  const handleDrop = useCallback(
    async (files: FileList) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      const phaseForDrop: PhaseId = phaseFilter ?? 'Ruw';
      let failed = 0;
      for (const file of list) {
        try {
          await upload.mutateAsync({ file, phaseId: phaseForDrop });
        } catch {
          failed += 1;
        }
      }
      const uploaded = list.length - failed;
      if (uploaded > 0) {
        toast.success(`${uploaded} bestand${uploaded === 1 ? '' : 'en'} geüpload.`);
      }
    },
    [phaseFilter, upload],
  );

  return (
    <AppFrame>
      <div className={`row ${styles.headerRow}`}>
        <div>
          <div className="eyebrow">Documenten · documentenbibliotheek</div>
          <h1 className={styles.title}>Documentenbibliotheek</h1>
        </div>
        <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
          <Btn>
            <Icon name="filter" size={14} /> Filters
          </Btn>
          <Btn variant="primary" onClick={() => setUploadOpen(true)}>
            <Icon name="upload" size={14} /> Uploaden
          </Btn>
        </div>
      </div>
      <DocumentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        defaultPhaseId={phaseFilter}
      />

      <div className={styles.body}>
        <SideNav ariaLabel="Documenten filters">
          <div className={`row gap-6 ${styles.search}`}>
            <Icon name="search" size={13} />
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Zoek in documenten…"
              aria-label="Zoek in documenten"
            />
          </div>
          <SideNavSection>Fase</SideNavSection>
          {PHASE_FILTERS.map((f) => (
            <SideNavItem
              key={f.label}
              label={f.label}
              icon={f.icon}
              active={phaseFilter === f.id}
              onClick={() => setPhaseFilter(f.id)}
            />
          ))}

          <SideNavSection>Tags</SideNavSection>
          <SideNavChips>
            {availableTags.length === 0 && (
              <span className="mono c-ink-3" style={{ fontSize: 11 }}>
                Nog geen tags
              </span>
            )}
            {availableTags.map((t) => {
              const active = tagFilter === t;
              return (
                <SideNavChip
                  key={t}
                  active={active}
                  onClick={() => setTagFilter(active ? undefined : t)}
                  ariaLabel={`Tag-filter ${t}`}
                >
                  <Pill kind={active ? 'accent' : 'idle'} dot={false}>
                    {t}
                  </Pill>
                </SideNavChip>
              );
            })}
          </SideNavChips>
        </SideNav>

        <Card
          className={`${styles.listCard} ${isDragging ? styles.listCardDragging : ''}`}
          onDragEnter={(e) => {
            if (e.dataTransfer.types.includes('Files')) {
              e.preventDefault();
              dragDepth.current += 1;
              setIsDragging(true);
            }
          }}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('Files')) {
              e.preventDefault();
            }
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            dragDepth.current = Math.max(0, dragDepth.current - 1);
            if (dragDepth.current === 0) setIsDragging(false);
          }}
          onDrop={(e) => {
            if (!e.dataTransfer.types.includes('Files')) return;
            e.preventDefault();
            dragDepth.current = 0;
            setIsDragging(false);
            void handleDrop(e.dataTransfer.files);
          }}
        >
          <QueryBoundary query={docs} loading="Documenten laden…">
            {(page) => (
              <>
                <div className={`row gap-12 ${styles.toolbar}`}>
                  <div className="row gap-6">
                    {phaseFilter && (
                      <Pill kind="accent" dot={false}>
                        {PHASE_LABEL[phaseFilter] ?? phaseFilter}
                      </Pill>
                    )}
                    {phaseFilter && (
                      <Btn
                        variant="ghost"
                        className={styles.clear}
                        onClick={() => setPhaseFilter(undefined)}
                      >
                        × wis
                      </Btn>
                    )}
                    {tagFilter && (
                      <Pill kind="accent" dot={false}>
                        #{tagFilter}
                      </Pill>
                    )}
                    {tagFilter && (
                      <Btn
                        variant="ghost"
                        className={styles.clear}
                        onClick={() => setTagFilter(undefined)}
                      >
                        × wis
                      </Btn>
                    )}
                  </div>
                  <span className={styles.results}>{toNumber(page.total)} resultaten</span>
                  {selected.size > 0 && (
                    <div className={styles.selectionBar}>
                      <span>{selected.size} geselecteerd</span>
                      <Btn
                        variant="ghost"
                        className={styles.clear}
                        onClick={() => setSelected(new Set())}
                      >
                        × wis
                      </Btn>
                      <Btn
                        variant="primary"
                        onClick={() => {
                          if (selected.size === 0) return;
                          downloadZip.mutate(Array.from(selected));
                        }}
                        disabled={downloadZip.isPending}
                      >
                        <Icon name="download" size={13} />{' '}
                        {downloadZip.isPending ? 'Inpakken…' : 'Download selectie (.zip)'}
                      </Btn>
                    </div>
                  )}
                  <div className={`ml-auto row gap-6 ${styles.sort}`}>
                    <span className={styles.sortLabel}>Sorteer:</span>
                    <span className={styles.sortValue}>Datum ↓</span>
                  </div>
                </div>

                <div className={`row gap-12 ${styles.tableHead}`}>
                  <div className={styles.colSelect}>
                    <input
                      type="checkbox"
                      className={styles.selectBox}
                      aria-label="Selecteer alle zichtbare documenten"
                      checked={page.items.length > 0 && page.items.every((d) => selected.has(d.id))}
                      ref={(el) => {
                        if (!el) return;
                        const allSelected = page.items.every((d) => selected.has(d.id));
                        const anySelected = page.items.some((d) => selected.has(d.id));
                        el.indeterminate = anySelected && !allSelected;
                      }}
                      onChange={(e) => {
                        const visible = page.items.map((d) => d.id);
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) {
                            for (const id of visible) next.add(id);
                          } else {
                            for (const id of visible) next.delete(id);
                          }
                          return next;
                        });
                      }}
                    />
                  </div>
                  <div className={styles.colName}>Naam</div>
                  <div className={styles.colPhase}>Fase</div>
                  <div className={styles.colVersion}>Versie</div>
                  <div className={styles.colSize}>Grootte</div>
                  <div className={styles.colDate}>Datum</div>
                </div>

                {page.items.map((row) => (
                  <DocumentRow
                    key={row.id}
                    doc={row}
                    selected={selected.has(row.id)}
                    onToggleSelect={toggleSelected}
                  />
                ))}

                {page.items.length === 0 && (
                  <div className="mono c-ink-3" style={{ padding: '24px' }}>
                    Geen documenten voor deze filter.
                  </div>
                )}

                <div className={`row gap-8 ${styles.dropHint}`}>
                  <Icon name="upload" size={14} />
                  <span>Sleep bestanden hier om toe te voegen</span>
                </div>
              </>
            )}
          </QueryBoundary>
        </Card>
      </div>
    </AppFrame>
  );
}

function DocumentRow({
  doc,
  selected,
  onToggleSelect,
}: {
  doc: DocumentDto;
  selected: boolean;
  onToggleSelect: (id: string) => void;
}) {
  const icon = iconForMime(doc.mime);
  const isPdf = icon === 'pdf';
  return (
    <Link
      to={`/documenten/${doc.id}`}
      className={`row gap-12 ${styles.docRow} ${selected ? styles.docRowSelected : ''}`}
    >
      <div className={styles.colSelect}>
        <input
          type="checkbox"
          className={styles.selectBox}
          aria-label={`Selecteer ${doc.name}`}
          checked={selected}
          onClick={(e) => {
            // Stop the parent <Link> from navigating when the checkbox is clicked.
            e.stopPropagation();
          }}
          onChange={(e) => {
            e.stopPropagation();
            onToggleSelect(doc.id);
          }}
        />
      </div>
      <div className={`row gap-10 ${styles.colName}`}>
        <div className={`${styles.docIcon} ${isPdf ? styles.docIconPdf : styles.docIconImg}`}>
          <Icon name={icon} size={14} color={isPdf ? 'var(--bad)' : 'var(--info)'} />
        </div>
        <span className={styles.docName}>{doc.name}</span>
        <ScanPill status={doc.scanStatus} />
        {(doc.tags ?? []).length > 0 && (
          <span className={`row gap-4 ${styles.rowTags}`}>
            {(doc.tags ?? []).map((t) => (
              <Pill key={t} kind="idle" dot={false}>
                {t}
              </Pill>
            ))}
          </span>
        )}
      </div>
      <div className={styles.colPhase}>
        <Pill kind="idle" dot={false}>
          {PHASE_LABEL[doc.phaseId] ?? doc.phaseId}
        </Pill>
      </div>
      <div className={`mono ${styles.colVersion} ${styles.cellMuted}`}>
        v{toNumber(doc.version)}
      </div>
      <div className={`mono ${styles.colSize} ${styles.cellMuted}`}>{formatBytes(doc.size)}</div>
      <div className={`mono ${styles.colDate} ${styles.cellMuted}`}>
        {formatDayMonth(doc.uploadedAt)}
      </div>
    </Link>
  );
}

function ScanPill({ status }: { status: ScanStatus }) {
  if (status === 'Clean') return null;
  if (status === 'Infected') {
    return (
      <Pill kind="bad" dot={false}>
        Geïnfecteerd
      </Pill>
    );
  }
  return (
    <Pill kind="warn" dot={false}>
      Scannen…
    </Pill>
  );
}

function iconForMime(mime: string): IconName {
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/zip') return 'archive';
  if (mime === 'application/pdf') return 'pdf';
  return 'paperclip';
}

function formatBytes(size: number | string): string {
  const n = toNumber(size);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
